<script setup>
const fullWidthMode = inject("fullWidthMode", false);
const halfCol = computed(() => (fullWidthMode ? undefined : 6));
import AIYongsin from './AIYongsin.vue';
import SajuEightSin from './SajuEightSin.vue';
import { useDeviceDetect } from '@/composables/useDeviceDetect';

const { isMobile } = useDeviceDetect();
import {
  parseKeywords,
  insertLineBreak,
  transformMarkedText,
  splitBySentence,
} from '@/composables/splitSentence';

const props = defineProps({
  yData: Object,
});
import { useAnalysisDataStore } from '@/store/modules/analysisDataStore';

const { transdata, birthData, birthJijanggan, curDaeun } = storeToRefs(useAnalysisDataStore());

const ManseBirthMainArea = ref(null);
const starRef = ref(null);
const langMode = ref('CHNKOR');
const birthTitle = ref('시일월년');
const showTooltip = ref(false);
// 신강 신약
const ssTitle = computed(() => props.yData?.분석상세?.신강신약여부?.신강여부);
const ssSubTitle = computed(() => props.yData?.분석상세?.신강신약여부?.신강여부해석);
const ssDefinition = computed(() => {
  const sentences = splitBySentence(props.yData?.분석상세?.신강신약해석?.개념특징?.개념 || '');
  return sentences.map((sentence) => transformMarkedText(sentence));
});
const ssAttribute = computed(() => {
  const sentences = splitBySentence(props.yData?.분석상세?.신강신약해석?.개념특징?.특징 || '');
  return sentences.map((sentence) => transformMarkedText(sentence));
});

// 인공지능 용신 분석
const modelConsensus = computed(() => {
  const sentences = splitBySentence(props.yData?.분석상세?.인공지능용신설명?.모델합의 || '');
  return sentences.map((sentence) => transformMarkedText(sentence));
});
const probabilitySummary = computed(() => {
  const sentences = splitBySentence(props.yData?.분석상세?.인공지능용신설명?.모델추천 || '');
  return sentences.map((sentence) => transformMarkedText(sentence));
});
const considerations = computed(() => {
  const sentences = splitBySentence(props.yData?.분석상세?.인공지능용신설명?.용신추천 || '');
  return sentences.map((sentence) => transformMarkedText(sentence));
});
// 성능지표
const metricAnalysisMain = computed(() =>
  transformMarkedText(props.yData?.분석상세?.성능지표?.지표해석1, { keywordClass: 'y-keyword' }),
);
const metricAnalysisSub = computed(() =>
  transformMarkedText(props.yData?.분석상세?.성능지표?.지표해석2, { keywordClass: 'y-keyword' }),
);

// 차트 데이터
const ssPosition = computed(() => props.yData?.분석상세?.신강신약기준?.신강신약위치);
const yongsinProbability = computed(() => props.yData?.분석상세?.인공지능용신통계?.모델별확률); // 막대그래프데이터
const fiveDistribution = computed(() => props.yData?.분석상세?.인공지능용신통계?.오행별분포); // 라벨 데이터

/** 성능지표 설명 열기/닫기 */
const openTooltip = () => {
  showTooltip.value = !showTooltip.value;
};
</script>

<template>
  <VRow class="full-height">
    <VCol cols="12" :md="halfCol" class="no-pad-col mobile-first">
      <VCard class="personality-card full-height right">
        <div class="detail-card__top-title yongsin content_chapter_area_title">
          <span class="detail-tit__num yongsin">6</span>
          <span class="detail-tit__txt yongsin">신강/신약</span>
        </div>
        <div class="personality-wrapper with-tit st-gap-24 content_info_area_box">
          <div class="person-content-box">
            <div class="ys-title-box">
              <div class="ys-title-box__title">{{ ssTitle }}</div>
              <div class="ys-title-box__desc">{{ ssSubTitle }}</div>
            </div>
          </div>
          <div class="yongsin__gg-box data-sec-01-box">
            <SajuEightSin
              ref="ManseBirthMainArea"
              :pr-birth-data="birthData"
              :pr-birth-jijanggan="birthJijanggan"
              :pr-lang-mode="langMode"
              :pr-birth-title="birthTitle"
              :pr-transdata="transdata"
              :pr-type="'birth'"
              :pr-cur-daeun="curDaeun"
              :selected-data="selectedData"
              :is-active="isActive"
              :pr-position-data="ssPosition"
            />
          </div>
          <div class="yg-line-01 mt-none"></div>
          <div class="yongsin-desc-box">
            <div class="yongsin-desc__tit">개념</div>
            <div v-for="(html, idx) in ssDefinition" :key="idx">
              <span class="yongsin-desc__txt" v-html="html"></span>
            </div>
          </div>
          <div class="yg-line-01 mt-none"></div>
          <div class="yongsin-desc-box">
            <div class="yongsin-desc__tit">특징</div>
            <div v-for="(html, idx) in ssAttribute" :key="idx">
              <span class="yongsin-desc__txt" v-html="html"></span>
            </div>
          </div>
        </div>
      </VCard>
    </VCol>
    <VCol cols="12" :md="halfCol" class="no-pad-col mobile-second">
      <VCard class="personality-card full-height">
        <div class="detail-card__top-title yongsin">
          <span class="detail-tit__num yongsin">7</span>
          <span class="detail-tit__txt yongsin">인공지능 용신 분석</span>
          <div class="explain-perf-indicator" :class="{ mobile: isMobile }" @click="openTooltip">
            성능지표
            <div
              v-if="showTooltip"
              class="explain-perf__tooltip"
              :class="[{ mobile: isMobile }, 'yongsin']"
            >
              <div class="perf-tooltip__top" v-html="metricAnalysisMain" />
              <div class="personality-divider" />
              <div class="perf-tooltip__bottom" v-html="metricAnalysisSub" />
            </div>
          </div>
        </div>

        <div class="personality-wrapper with-tit st-gap-24 content_info_area_box">
          <div class="ys-img-0402-box">
            <AIYongsin
              :yongsinProbability="yongsinProbability"
              :fiveDistribution="fiveDistribution"
              :isMobile="isMobile"
            />
          </div>
          <div class="yg-line-01 mt-none"></div>
          <div class="yongsin-desc-box">
            <div class="yongsin-desc__tit">모델 합의</div>
            <div v-for="(html, idx) in modelConsensus" :key="idx">
              <span class="yongsin-desc__txt" v-html="html"></span>
            </div>
          </div>
          <div class="yg-line-01 mt-none"></div>
          <div class="yongsin-desc-box">
            <div class="yongsin-desc__tit">모델 추천</div>
            <div v-for="(html, idx) in probabilitySummary" :key="idx">
              <span class="yongsin-desc__txt" v-html="html"></span>
            </div>
          </div>
          <!-- <div class="yg-line-01 mt-none"></div>
          <div class="yongsin-desc-box">
            <div class="yongsin-desc__tit">
              용신 추천
            </div>
            <div v-for="(html, idx) in considerations" :key="idx">
              <span class="yongsin-desc__txt" v-html="html"></span>
            </div>
          </div> -->
        </div>
      </VCard>
    </VCol>
  </VRow>
</template>
<style scoped lang="scss">
@import '@/assets/nangman_style/_util.scss';
@import '@/assets/nangman_style/youngsin.scss';
</style>
